import type { ReactNode } from 'react'

type CreateMeetingSectionProps = {
  /** 1, 2, 3 — shown in the badge and read out as part of the heading. */
  step: number
  title: string
  children: ReactNode
  testId?: string
}

/**
 * One numbered step of the Create Meeting form.
 *
 * The form asks for three different kinds of thing — what the meeting is,
 * what it listens to, and what language it works in — and used to run all of
 * them together as one grid of fields with nothing between them. The numbers
 * are not decoration: this is a long form, and they are what says how much of
 * it is left.
 *
 * The badge is `aria-hidden` and the number repeated in the heading text, so
 * a screen reader reads "Step 2, Meeting type" once rather than "2" and then
 * the title as two unrelated things.
 */
export default function CreateMeetingSection({
  step,
  title,
  children,
  testId,
}: CreateMeetingSectionProps) {
  return (
    <section data-testid={testId} className="space-y-2.5">
      <h3 className="flex items-center gap-2 text-[15px] font-semibold text-[var(--text-primary)]">
        <span
          aria-hidden="true"
          className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[var(--accent-light)] text-xs font-bold text-[var(--accent)]"
        >
          {step}
        </span>
        {title}
      </h3>
      {children}
    </section>
  )
}
